import type {
  Conversation,
  AnalysisResponse,
  GenerateDraftRequest,
  GenerateDraftResponse,
  ChatResponse,
  SubmitFeedbackRequest,
  PluginSession,
  CustomerNote,
  CreateNoteRequest,
  Sentiment,
  Draft,
  Article,
} from './types';

const API_URL = import.meta.env.VITE_API_URL || 'http://api.crewflow.local';

/**
 * Custom API Error with status code
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Check if error is network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError &&
    (error.message.includes('fetch') ||
      error.message.includes('network') ||
      error.message.includes('Failed to fetch'))
  );
}

/**
 * Check if error is timeout
 */
export function isTimeoutError(error: unknown): boolean {
  return error instanceof Error && error.message.includes('timeout');
}

/**
 * Check if user is offline
 */
export function isOffline(): boolean {
  return !navigator.onLine;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private accountId: string | null = null;
  private platformUserId: string | null = null;
  private platformUserEmail: string | null = null;
  private platformUserName: string | null = null;
  private defaultTimeout = 30000; // 30 seconds

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setToken(token: string) {
    this.token = token;
  }

  setAccountId(accountId: string) {
    this.accountId = accountId;
  }

  getAccountId(): string | null {
    return this.accountId;
  }

  setPlatformUser(userId: string, email: string, name: string) {
    console.log('[ApiClient] Setting platform user:', { userId, email, name });
    this.platformUserId = userId;
    this.platformUserEmail = email;
    this.platformUserName = name;
  }

  getPlatformUser(): { userId: string | null; email: string | null; name: string | null } {
    return {
      userId: this.platformUserId,
      email: this.platformUserEmail,
      name: this.platformUserName,
    };
  }

  private async fetch<T>(
    endpoint: string,
    options?: RequestInit & { timeout?: number }
  ): Promise<T> {
    // Check if offline before making request
    if (isOffline()) {
      throw new ApiError('You are offline. Check your internet connection.', 0);
    }

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...(this.accountId && { 'X-Account-ID': this.accountId }),
      ...(this.platformUserId && { 'X-Platform-User-ID': this.platformUserId }),
      ...(this.platformUserEmail && { 'X-Platform-User-Email': this.platformUserEmail }),
      ...(this.platformUserName && { 'X-Platform-User-Name': this.platformUserName }),
      ...options?.headers,
    };

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeout = options?.timeout || this.defaultTimeout;
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
        credentials: 'include', // Include cookies for session
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Handle non-OK responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'Unknown error',
        }));

        // Handle specific status codes
        if (response.status === 401) {
          // Unauthorized - DON'T auto-redirect in plugin (it's embedded in iframe)
          // Just throw error and let component handle it
          console.warn('[ApiClient] 401 Unauthorized:', endpoint, errorData);
          throw new ApiError(
            errorData.message || 'Unauthorized - authentication required',
            401,
            errorData
          );
        }

        if (response.status === 403) {
          throw new ApiError(
            "You don't have permission to perform this action.",
            403,
            errorData
          );
        }

        if (response.status === 404) {
          throw new ApiError(
            errorData.message || 'Resource not found',
            404,
            errorData
          );
        }

        if (response.status === 429) {
          throw new ApiError(
            'Too many requests. Please wait a moment and try again.',
            429,
            errorData
          );
        }

        if (response.status >= 500) {
          throw new ApiError(
            errorData.message || 'Server error. Please try again later.',
            response.status,
            errorData
          );
        }

        // Generic error for other status codes
        throw new ApiError(
          errorData.message || `Request failed with status ${response.status}`,
          response.status,
          errorData
        );
      }

      // Handle 204 No Content or empty responses
      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as T;
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle abort (timeout)
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timed out. Please try again.', 408);
      }

      // Handle network errors
      if (isNetworkError(error)) {
        throw new ApiError('Network error. Please check your connection.', 0);
      }

      // Re-throw ApiErrors as-is
      if (error instanceof ApiError) {
        throw error;
      }

      // Wrap unknown errors
      throw new ApiError(
        error instanceof Error ? error.message : 'An unexpected error occurred'
      );
    }
  }

  async analyzeConversation(conversation: Conversation): Promise<AnalysisResponse> {
    // Send full conversation data for analysis
    return this.fetch<AnalysisResponse>('/plugin/conversations/analyze', {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversation.id,
        conversation: conversation,
      }),
    });
  }

  async generateDraft(request: GenerateDraftRequest): Promise<GenerateDraftResponse> {
    return this.fetch<GenerateDraftResponse>('/plugin/drafts/generate', {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: request.conversation_id,
        answers: request.answers,
        tone: request.tone,
        help_center_name: 'helpdocs', // Default to helpdocs
      }),
    });
  }

  async regenerateDraft(
    draftId: string,
    options: { tone?: string; length?: string; comment?: string }
  ): Promise<GenerateDraftResponse> {
    return this.fetch<GenerateDraftResponse>(`/plugin/drafts/${draftId}/regenerate`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async chat(
    conversationId: string,
    message: string,
    context: {
      conversation: Conversation;
      sentiment?: Sentiment;
      currentDraft?: Draft;
      articles?: Article[];
    }
  ): Promise<ChatResponse> {
    return this.fetch<ChatResponse>(`/plugin/conversations/${conversationId}/chat`, {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversation: context.conversation,
        messages: context.conversation.messages,
        sentiment: context.sentiment,
        currentDraft: context.currentDraft,
        articles: context.articles,
      }),
    });
  }

  async submitFeedback(request: SubmitFeedbackRequest): Promise<void> {
    await this.fetch(`/plugin/drafts/${request.draft_id}/feedback`, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: request.conversation_id,
        action: request.action,
        final_text: request.final_text,
        feedback_text: request.feedback_text,
        time_to_decision: request.time_to_decision,
        regeneration_reason: request.regeneration_reason,
      }),
    });
  }

  async getSession(): Promise<PluginSession> {
    return this.fetch<PluginSession>('/plugin/session', {
      method: 'GET',
    });
  }

  // Customer Notes API
  async getCustomerNotes(email: string): Promise<{ notes: CustomerNote[] }> {
    return this.fetch<{ notes: CustomerNote[] }>(
      `/plugin/customers/${encodeURIComponent(email)}/notes`,
      {
        method: 'GET',
      }
    );
  }

  async createCustomerNote(
    email: string,
    note: CreateNoteRequest
  ): Promise<{ note: CustomerNote }> {
    return this.fetch<{ note: CustomerNote }>(
      `/plugin/customers/${encodeURIComponent(email)}/notes`,
      {
        method: 'POST',
        body: JSON.stringify(note),
      }
    );
  }

  async updateCustomerNote(
    noteId: string,
    note: string
  ): Promise<{ note: CustomerNote }> {
    return this.fetch<{ note: CustomerNote }>(`/plugin/customers/notes/${noteId}`, {
      method: 'PUT',
      body: JSON.stringify({ note }),
    });
  }

  async deleteCustomerNote(noteId: string): Promise<void> {
    await this.fetch(`/plugin/customers/notes/${noteId}`, {
      method: 'DELETE',
    });
  }
}

export const apiClient = new ApiClient(API_URL);
